// Shared arc state — the toolkit profile, the chosen methodology,
// the parsed script, the character pick, and the analysis result —
// survives navigation between the seven welcome-arc routes.
//
// In-memory only. Persistence (Postgres / lighter) lands in directive #5
// after a data-shape audit (the journal is paper-primary; the persisted
// shape is small, so Supabase's auth coupling probably outweighs what's
// needed — flagged for separate review).
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import { AnalysisResult, ParsedProject, QuizResult } from "../api/client";

// Rich line-level shape — matches the backend ParsedScript dataclass in
// src/cloud_parse.py. Includes action lines + scenes + diagnostics, the
// rest of the arc only needs the minimal `parsedProject` shape but the
// script-with-callouts surface walks the rich `lines[]` directly so it can
// render stage directions and scene starts.
export interface RichParsedLine {
  id: number;
  order_in_scene: number;
  character: string | null;
  text: string;
  delivery_hints: string[];
  page: number;
  is_dual_dialogue: boolean;
}
export interface RichParsedScene {
  order_index: number;
  name: string;
  descriptive_label: string;
  line_ids: number[];
  start_page: number;
}
export interface RichParsedScript {
  title: string;
  characters: string[];
  lines: RichParsedLine[];
  scenes: RichParsedScene[];
  diagnostics: Record<string, unknown>;
}

export interface ArcState {
  // The quiz answers in flight (question_id -> option_key), and the placed
  // result. The result's lineage.analyzer_methodology_id drives the downstream
  // analyzer; methodologyId mirrors it so the rest of the arc is unchanged.
  quizAnswers: Record<string, string>;
  quizResult: QuizResult | null;
  methodologyId: string | null;
  parsedProject: ParsedProject | null;
  parsedScript: RichParsedScript | null;   // rich shape — for script-with-callouts
  projectId: string | null;                 // stable id for /annotations + /imported
  selectedCharacter: string | null;
  analysis: AnalysisResult | null;
  // The actor's most recent rehearsal take for this project. Set by the
  // Recording Booth on stop+upload; read by Run Lines for playback.
  takeId: string | null;
}

const initialState: ArcState = {
  quizAnswers: {},
  quizResult: null,
  methodologyId: null,
  parsedProject: null,
  parsedScript: null,
  projectId: null,
  selectedCharacter: null,
  analysis: null,
  takeId: null,
};

interface ArcContextValue {
  state: ArcState;
  update: (patch: Partial<ArcState>) => void;
  reset: () => void;
}

const ArcContext = createContext<ArcContextValue | null>(null);

export function ArcProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ArcState>(initialState);
  const update = useCallback(
    (patch: Partial<ArcState>) => setState((prev) => ({ ...prev, ...patch })),
    [],
  );
  const reset = useCallback(() => setState(initialState), []);
  return (
    <ArcContext.Provider value={{ state, update, reset }}>
      {children}
    </ArcContext.Provider>
  );
}

export function useArc(): ArcContextValue {
  const ctx = useContext(ArcContext);
  if (!ctx) throw new Error("useArc must be used inside <ArcProvider>");
  return ctx;
}
